import React from 'react';

/**
 * SettingsQuickBooks - QuickBooks integration tab component
 * Contains: QB settings form, sync controls, mappings link
 */
const SettingsQuickBooks = ({
  qbSettings,
  setQbSettings,
  qbTaxCodes,
  qbQueueStats,
  onSaveSettings,
  onSync,
  onDownloadQwc,
  onRefreshStatus,
  loading,
  syncing
}) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSaveSettings(qbSettings);
  };

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2>QuickBooks Desktop Integration</h2>
      </div>

      <form onSubmit={handleSubmit} className="settings-form">
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={qbSettings.qb_enabled === 'true'}
              onChange={(e) => setQbSettings({
                ...qbSettings,
                qb_enabled: e.target.checked ? 'true' : 'false'
              })}
            />
            <span>Enable QuickBooks Integration</span>
          </label>
        </div>

        <div className="form-group">
          <label>Web Connector Password</label>
          <input
            type="password"
            value={qbSettings.qb_wc_password}
            onChange={(e) => setQbSettings({ ...qbSettings, qb_wc_password: e.target.value })}
            placeholder="Set password for Web Connector"
          />
          <small>Used by QuickBooks Web Connector to authenticate</small>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={qbSettings.qb_auto_invoice === 'true'}
              onChange={(e) => setQbSettings({
                ...qbSettings,
                qb_auto_invoice: e.target.checked ? 'true' : 'false'
              })}
            />
            <span>Auto-create invoices when shipments are marked shipped</span>
          </label>
        </div>

        <div className="form-group">
          <label>Default Tax Code</label>
          <select
            value={qbSettings.qb_default_tax_code}
            onChange={(e) => setQbSettings({ ...qbSettings, qb_default_tax_code: e.target.value })}
          >
            <option value="E">E - Exempt</option>
            <option value="S">S - Standard</option>
            {qbTaxCodes.map(tc => (
              <option key={tc.list_id} value={tc.name}>
                {tc.name} {tc.rate > 0 ? `(${tc.rate}%)` : ''}
              </option>
            ))}
          </select>
          <small>Tax code applied to invoice line items</small>
        </div>

        <button type="submit" className="save-btn" disabled={loading}>
          {loading ? 'Saving...' : 'Save Settings'}
        </button>
      </form>

      <div className="qb-actions-section">
        <h3>Web Connector Setup</h3>
        <p>Download the .qwc file and add it to QuickBooks Web Connector on your office PC.</p>
        <button className="qb-action-btn" onClick={onDownloadQwc}>
          Download .qwc File
        </button>
      </div>

      <div className="qb-actions-section">
        <h3>Sync Status</h3>
        {qbSettings.qb_last_sync && (
          <div className="qb-last-sync-box">
            <span className="sync-label">Last Sync:</span>
            <span className="sync-time">{new Date(qbSettings.qb_last_sync).toLocaleString()}</span>
          </div>
        )}
        <div className="qb-stats">
          <div className="qb-stat">
            <span className="qb-stat-label">Pending:</span>
            <span className="qb-stat-value">{qbQueueStats.pending || 0}</span>
          </div>
          <div className="qb-stat">
            <span className="qb-stat-label">Completed:</span>
            <span className="qb-stat-value">{qbQueueStats.completed || 0}</span>
          </div>
          <div className="qb-stat">
            <span className="qb-stat-label">Failed:</span>
            <span className="qb-stat-value qb-stat-failed">{qbQueueStats.failed || 0}</span>
          </div>
        </div>
        <div className="qb-action-buttons">
          <button
            type="button"
            className="qb-action-btn secondary"
            onClick={onRefreshStatus}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh Status'}
          </button>
          <button
            type="button"
            className="qb-action-btn"
            onClick={onSync}
            disabled={syncing || qbSettings.qb_enabled !== 'true'}
          >
            {syncing ? 'Queuing...' : 'Queue Full Sync'}
          </button>
        </div>
        <small>Queues sync for customers, items, and tax codes. Connect Web Connector to complete.</small>
      </div>

      <div className="qb-actions-section">
        <h3>Mappings</h3>
        <p>Map your customers and items to QuickBooks entities.</p>
        <a href="/quickbooks" className="qb-action-btn" style={{ textDecoration: 'none', display: 'inline-block' }}>
          Open Mappings Page
        </a>
      </div>
    </div>
  );
};

export default SettingsQuickBooks;
